/**
 * Literafan Image Optimizer & Delivery Utility
 * 
 * Ensures 100% reliable image loading across all hosting environments (including Hostinger Apache/PHP shared hosting),
 * eliminating broken proxy dependencies, while optimizing heavy background assets and ensuring fast asynchronous rendering.
 */

// Mapping of known heavy PNG/JPG assets to local lightweight WebP alternatives
const LOCAL_OPTIMIZED_ASSETS: Record<string, string> = {
  // Categorias e fundos
  'https://i.postimg.cc/YCCFNSzs/criticas.png': '/images/criticas.webp',
  'https://i.postimg.cc/CLFnYKXZ/biografias.png': '/images/biografias.webp',
  'https://i.postimg.cc/xCTSWs96/F1.avif': '/images/bg-f1.webp',
  'https://i.postimg.cc/Y9KhwBvS/F2.avif': '/images/bg-f2.webp',
  
  // Artigos de alta densidade (economia > 26MB no mobile)
  'https://i.postimg.cc/d0LdMr3J/Bilu-3.png': '/images/bilu-3.webp',
  'https://i.postimg.cc/rwVS4KZb/et-bilu-c4.png': '/images/et-bilu-c4.webp',
  'https://i.postimg.cc/vHbtC33w/ET-Bilu-EP-1.png': '/images/et-bilu-ep1.webp',
  'https://i.postimg.cc/2ymdBCSv/capa-romantismo.png': '/images/capa-romantismo.webp',
  'https://i.postimg.cc/yd4YnBXC/arcadismo-capa.png': '/images/arcadismo-capa.webp',
  'https://i.postimg.cc/J4jXbd4z/Ca1.png': '/images/ca1.webp',
  'https://i.postimg.cc/PJ8gGs4k/quinhentismo.png': '/images/quinhentismo.webp',
  'https://i.postimg.cc/pLbd6Lbz/adaptacoes.png': '/images/adaptacoes.webp',
  'https://i.postimg.cc/50WTpK2Z/Pronome-neutro-o-dialeto-pseudointelectual.png': '/images/pronome-neutro.webp',
  'https://i.postimg.cc/dt5W5bcd/Nobel-26.png': '/images/nobel-26.webp',
  'https://i.postimg.cc/7hPRLghF/capa-artigo.png': '/images/capa-artigo.webp',
  'https://i.postimg.cc/mDyfyzJs/capa-inteligenncia-artificial.png': '/images/capa-ia.webp',
  'https://i.postimg.cc/90Hr1h61/bilu-2.jpg': '/images/bilu-2.webp',
  'https://i.postimg.cc/YSwd0L50/capa.avif': '/images/capa-2026.webp',
  'https://i.postimg.cc/fb53C2Mk/df8e9873-c8bf-4c74-a690-e26e2fe62f1c.png': '/images/df8e9873.webp',
  'https://i.postimg.cc/tgBnPCFg/como-dominar-a-escrita-criativa.png': '/images/como-dominar-a-escrita-criativa.webp',
  'https://i.postimg.cc/PqShJGcQ/c1-mv0Pwg.png': '/images/c1-mv0Pwg.webp',

  // Identidade visual & ícones
  'https://i.postimg.cc/x8dQw63d/Nova-logo-Edited-1.png': '/images/nova-logo.webp',
  'https://i.postimg.cc/nztcj49c/logo-kick-4.png': '/images/logo-kick.webp',
};

/**
 * Resolves the optimal, direct, production-safe image URL.
 * Automatically substitutes heavy multi-megabyte PNGs with ultra-fast local WebP files.
 */
export function getOptimizedImageUrl(url?: string | null): string {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed) return '';

  // 1. Unwrap any legacy or cached /api/image-proxy?url=... back to the direct CDN URL
  if (trimmed.includes('/api/image-proxy')) {
    try {
      const match = trimmed.match(/[?&]url=([^&]+)/);
      if (match && match[1]) {
        const decoded = decodeURIComponent(match[1]);
        return getOptimizedImageUrl(decoded);
      }
    } catch {}
  }

  // 2. Normalize relative image paths (e.g. images/... or public/images/...) to absolute root paths (/images/...)
  let cleanPath = trimmed;
  if (cleanPath.startsWith('public/images/')) {
    cleanPath = '/' + cleanPath.replace(/^public\//, '');
  } else if (cleanPath.startsWith('images/')) {
    cleanPath = '/' + cleanPath;
  } else if (cleanPath.startsWith('./images/')) {
    cleanPath = '/' + cleanPath.replace(/^\.\//, '');
  }

  if (cleanPath.startsWith('/') || cleanPath.startsWith('data:') || cleanPath.startsWith('blob:')) {
    return cleanPath;
  }

  // 3. Map heavy 2-3MB PNGs to pre-compressed local WebP alternatives (~20-80KB)
  if (LOCAL_OPTIMIZED_ASSETS[trimmed]) {
    return LOCAL_OPTIMIZED_ASSETS[trimmed];
  }
  if (LOCAL_OPTIMIZED_ASSETS[cleanPath]) {
    return LOCAL_OPTIMIZED_ASSETS[cleanPath];
  }

  // 4. Case-insensitive or stripped query parameter check for mapping
  const baseUri = trimmed.split('?')[0];
  if (LOCAL_OPTIMIZED_ASSETS[baseUri]) {
    return LOCAL_OPTIMIZED_ASSETS[baseUri];
  }

  // 5. Direct return
  return cleanPath;
}

/**
 * Optimizes all <img> tags inside an HTML string (such as article content)
 * Ensures:
 * - Unwrapping any legacy /api/image-proxy links to direct CDN URLs
 * - loading="lazy" for non-blocking page downloads
 * - decoding="async" for smooth main-thread painting
 * - referrerpolicy="no-referrer" to prevent CDN referrer blocks
 * - Smooth styling classes
 */
export function optimizeHtmlImages(html?: string | null): string {
  if (!html) return '';

  let imgIndex = 0;
  return html.replace(/<img\b([^>]*)>/gi, (match, attrs) => {
    let newAttrs = attrs;
    imgIndex++;

    // 1. Clean up src attribute: resolve proxy and optimize known assets
    newAttrs = newAttrs.replace(/src=(["'])(.*?)(["'])/gi, (_: string, q1: string, src: string, q2: string) => {
      return `src=${q1}${getOptimizedImageUrl(src)}${q2}`;
    });

    // 2. Ensure loading="lazy" for all images after the first hero
    if (!/loading=/i.test(newAttrs)) {
      newAttrs += imgIndex === 1 ? ' loading="eager"' : ' loading="lazy"';
    }

    // 3. Ensure decoding="async"
    if (!/decoding=/i.test(newAttrs)) {
      newAttrs += ' decoding="async"';
    }

    // 4. Ensure referrerpolicy="no-referrer" for reliable CDN image delivery
    if (!/referrerpolicy=/i.test(newAttrs)) {
      newAttrs += ' referrerpolicy="no-referrer"';
    }

    return `<img ${newAttrs.trim()}>`;
  });
}
